import { Sun, Plus, Check, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import CurrentlyViewing from "./CurrentlyViewing";

// Summer Glow Event Images
const SPRING_FLOWERS_AD = "https://customer-assets.emergentagent.com/job_21ce227c-a119-454a-bc38-a3258dfd6c74/artifacts/nf58p9tq_641512314_1540464164746314_8539733254831413073_n.jpg";
const KARINE_CLINIC_IMAGE = "https://customer-assets.emergentagent.com/job_21ce227c-a119-454a-bc38-a3258dfd6c74/artifacts/zygbvorq_11d9539a-ce47-496f-a9c4-5cffeefaea67.jpg";
const SPRING_GLOW_IMAGE = "https://customer-assets.emergentagent.com/job_2efc821e-9d26-4455-8d0b-5ad85610e85f/artifacts/vgchmko9_800x1200.png";

const PromotionsSection = ({ scrollToSection, openBookingModal }) => {
  const { t, language } = useLanguage();
  const { addToCart, isInCart, removeFromCart } = useCart();

  // Translations for promo tags
  const tagTranslations = {
    "Most Popular": { en: "Most Popular", fr: "Plus Populaire" },
    "Complete Package": { en: "Complete Package", fr: "Forfait Complet" },
    "Quick & Easy": { en: "Quick & Easy", fr: "Rapide et Facile" },
    "Best Value": { en: "Best Value", fr: "Meilleur Rapport" },
    "Bundle Deal": { en: "Bundle Deal", fr: "Offre Groupée" },
    "Best Pricing": { en: "Best Pricing", fr: "Meilleur Prix" },
  };

  const springRefreshPackages = [
    {
      id: "promo-sr1",
      title: { en: "11's Package", fr: "Forfait 11's" },
      subtitle: { en: "Brow Lift or Lip Flip", fr: "Lifting Sourcils ou Lip Flip" },
      areas: { 
        en: "11's (Brow Lift or Lip Flip)", 
        fr: "11's (Lifting Sourcils ou Lip Flip)" 
      },
      price: "$299",
      note: { en: "*May substitute 11's for Forehead", fr: "*Possibilité de substituer 11's pour Front" },
      tag: null,
      category: "Summer Refresh Package"
    },
    {
      id: "promo-sr2",
      title: { en: "Mini Refresh", fr: "Mini Rafraîchissement" },
      subtitle: { en: "Summer Refresh 2", fr: "Rafraîchissement Été 2" },
      areas: { 
        en: "Crow's Feet • Forehead • Brow Lift", 
        fr: "Pattes d'Oie • Front • Lifting Sourcils" 
      },
      price: "$399",
      note: { en: "*Lip Flip may substitute Brow Lift", fr: "*Lip Flip peut substituer Lifting Sourcils" },
      tag: null,
      category: "Summer Refresh Package"
    },
    {
      id: "promo-sr3",
      title: { en: "The Big 3 Areas", fr: "Les 3 Grandes Zones" },
      subtitle: { en: "Best Value", fr: "Meilleur Rapport" },
      areas: { 
        en: "Crow's Feet • 11's • Forehead", 
        fr: "Pattes d'Oie • 11's • Front" 
      },
      price: "$499",
      note: null,
      tag: "Best Value",
      category: "Summer Refresh Package"
    },
    {
      id: "promo-sr4",
      title: { en: "Most Popular Package", fr: "Forfait Plus Populaire" },
      subtitle: { en: "Summer Refresh 4", fr: "Rafraîchissement Été 4" },
      areas: { 
        en: "Forehead • Crow's Feet • 11's • Brow Lift • Smile Lift • Bunny Lines • Lip Flip", 
        fr: "Front • Pattes d'Oie • 11's • Lifting Sourcils • Lifting Sourire • Lignes du Lapin • Lip Flip" 
      },
      price: "$599",
      note: null,
      tag: "Most Popular",
      category: "Summer Refresh Package"
    },
    {
      id: "promo-sr5",
      title: { en: "Total Face Package", fr: "Forfait Visage Complet" },
      subtitle: { en: "The Total Face", fr: "Visage Complet" },
      areas: { 
        en: "Forehead • Crow's Feet • 11's • Brow Lift • Smile Lift • Bunny Lines • Lip Flip • Neck Bands", 
        fr: "Front • Pattes d'Oie • 11's • Lifting Sourcils • Lifting Sourire • Lignes du Lapin • Lip Flip • Bandes du Cou" 
      },
      price: "$799",
      note: null,
      tag: "Complete Package",
      category: "Summer Refresh Package"
    },
  ];

  const fillerDeals = [
    {
      id: "promo-f0",
      title: { en: "Half Syringe", fr: "Demi Seringue" },
      subtitle: { en: "Lips or Any Area", fr: "Lèvres ou Toute Zone" },
      price: "$299",
      tag: null,
      category: "Summer Filler Deal"
    },
    {
      id: "promo-f1",
      title: { en: "One Syringe", fr: "Une Seringue" },
      subtitle: { en: "Any Area", fr: "Toute Zone" },
      price: "$499",
      tag: null,
      category: "Summer Filler Deal"
    },
    {
      id: "promo-f2",
      title: { en: "Two Syringes", fr: "Deux Seringues" },
      subtitle: { en: "Any Area", fr: "Toute Zone" },
      price: "$950",
      tag: null,
      category: "Summer Filler Deal"
    },
    {
      id: "promo-f3",
      title: { en: "Three Syringes", fr: "Trois Seringues" },
      subtitle: { en: "Any Area", fr: "Toute Zone" },
      price: "$1,350",
      tag: null,
      category: "Summer Filler Deal"
    },
    {
      id: "promo-f4",
      title: { en: "Four Syringes", fr: "Quatre Seringues" },
      subtitle: { en: "Any Area", fr: "Toute Zone" },
      price: "$1,599",
      tag: null,
      category: "Summer Filler Deal"
    },
    {
      id: "promo-f5",
      title: { en: "Six Syringes", fr: "Six Seringues" },
      subtitle: { en: "Great Value", fr: "Excellente Valeur" },
      price: "$2,199",
      tag: null,
      category: "Summer Filler Deal"
    },
    {
      id: "promo-f6",
      title: { en: "Eight Syringes", fr: "Huit Seringues" },
      subtitle: { en: "Premium Package", fr: "Forfait Premium" },
      price: "$2,899",
      tag: null,
      category: "Summer Filler Deal"
    },
    {
      id: "promo-f7",
      title: { en: "10 Syringes", fr: "10 Seringues" },
      subtitle: { en: "Best Value", fr: "Meilleur Rapport" },
      price: "$3,500",
      tag: "Best Value",
      category: "Summer Filler Deal"
    },
  ];

  const handlePackageClick = (pkg) => {
    const titleText = typeof pkg.title === 'object' ? pkg.title.en : pkg.title;
    const subtitleText = pkg.subtitle ? (typeof pkg.subtitle === 'object' ? pkg.subtitle.en : pkg.subtitle) : null;
    const areasText = pkg.areas ? (typeof pkg.areas === 'object' ? pkg.areas.en : pkg.areas) : null;
    
    const cartItem = {
      id: pkg.id,
      name: subtitleText ? `${titleText} - ${subtitleText}` : titleText,
      price: pkg.price,
      category: pkg.category,
      description: areasText || subtitleText
    };
    
    if (isInCart(pkg.id)) {
      removeFromCart(pkg.id);
    } else {
      addToCart(cartItem);
    }
  };

  // Helper function to get translated text
  const getText = (textObj) => {
    if (typeof textObj === 'object' && textObj !== null) {
      return textObj[language] || textObj.en;
    }
    return textObj;
  };

  // Helper function to get translated tag
  const getTag = (tag) => {
    if (!tag) return null;
    return tagTranslations[tag]?.[language] || tag;
  };

  return (
    <section
      id="promotions"
      className="py-12 px-6 bg-gradient-to-b from-pink-50 to-background"
      data-testid="promotions-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Summer Glow Event Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 px-4 py-2 rounded-full mb-4">
            <Sun className="w-4 h-4" />
            <span className="font-mono text-xs tracking-widest uppercase">
              {language === "fr" ? "ÉVÉNEMENT ÉCLAT D'ÉTÉ EN COURS" : "SUMMER GLOW EVENT IS HERE"}
            </span>
            <span>🌸</span>
          </div>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-4 text-pink-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {language === "fr" ? "Éclat d'" : "Summer "} 
            <span className="italic text-pink-600">{language === "fr" ? "Été" : "Flowers"}</span>
            {" "}{language === "fr" ? "Événement" : "Event"}
          </h2>
          <p className="text-muted-foreground text-sm max-w-2xl mx-auto leading-relaxed">
            {language === "fr" 
              ? "L'été est la saison du rayonnement — et le moment idéal pour rafraîchir votre look, adoucir les lignes et restaurer le volume naturel pour chaque événement de votre calendrier."
              : "Summer is the season of radiance — and the perfect time to refresh your look, soften lines, and restore natural volume for every event on your calendar."}
          </p>
          
          {/* Currently Viewing Badge */}
          <div className="flex justify-center mt-4">
            <CurrentlyViewing variant="badge" />
          </div>
        </div>

        {/* NEW · Filler-by-Area flyer — Iter 380 Feb 20 2026 · Karine's ask:
            surface the new by-area filler menu on the homepage promo strip
            so first-time visitors see it before they even click Promotions. */}
        <div className="mb-8 rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-300"
             data-testid="home-filler-by-area-banner">
          <Link to="/dermal-fillers#pricing-by-area" className="block group relative">
            <picture>
              <source srcSet="/promos/filler-by-area-pricing-2026.webp" type="image/webp" />
              <img
                src="/promos/filler-by-area-pricing-2026.png"
                alt={language === "fr"
                  ? "Nouvelle grille tarifaire par zone pour les fillers — 18 forfaits transparents"
                  : "New by-area filler pricing menu — 18 transparent packages"}
                className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500"
                loading="lazy"
              />
            </picture>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-6 py-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-white">
                  <div className="text-[10px] uppercase tracking-widest font-black text-amber-300">
                    {language === "fr" ? "Nouveau · Février 2026" : "Now Introducing · February 2026"}
                  </div>
                  <div className="text-lg sm:text-xl font-serif" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {language === "fr" ? "Fillers par zone · 18 forfaits transparents" : "Fillers by Area · 18 transparent packages"}
                  </div>
                  <div className="text-xs text-white/80 mt-0.5">
                    {language === "fr" ? "Aucun choc au moment de payer · aucun coût imprévu." : "No sticker shock. No unknown costs."}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 text-amber-950 text-xs font-bold shadow group-hover:bg-amber-300 transition">
                  {language === "fr" ? "Voir tous les prix" : "See all pricing"} →
                </span>
              </div>
            </div>
          </Link>
        </div>

        {/* Summer Event Banners — golden-hour treatment applied on top of
            existing imagery until fresh summer photography is uploaded.
            Left banner uses a warm sun-glow filter + amber palette; right
            banner keeps its neutral Summer Glow shot. */}
        <div className="mb-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Summer Glow Ad - Left (warm filter applied to existing shot) */}
          <div className="rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border-2 border-amber-300 flex flex-col">
            <div className="flex-1 relative">
              <img 
                src={SPRING_FLOWERS_AD}
                alt={language === "fr" ? "Événement Éclat d'Été - Botox" : "Summer Glow Event - Botox"}
                className="w-full h-full object-cover object-top min-h-[320px]"
                style={{ filter: "brightness(1.06) saturate(1.15) hue-rotate(-14deg) contrast(1.04)" }}
                data-testid="spring-flowers-banner-left"
              />
              {/* Golden-hour gradient wash — from warm gold top-left to
                  transparent so it reads sunlit-summer without hiding the
                  underlying photo. */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(251,191,36,0.28) 0%, rgba(251,146,60,0.14) 40%, rgba(255,255,255,0) 70%)",
                  mixBlendMode: "soft-light",
                }}
              />
            </div>
            <div className="p-3 bg-white">
              <button onClick={openBookingModal}
                className="block w-full text-center bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md cursor-pointer"
                data-testid="promo-book-now-left">
                {language === "fr" ? "Reserver Maintenant" : "Book Now"}
              </button>
            </div>
          </div>
          
          {/* Karine at Clinic - Center */}
          <div className="rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border-2 border-amber-300 bg-amber-50 flex flex-col">
            <div className="p-4 text-center flex-1">
              <img 
                src={KARINE_CLINIC_IMAGE}
                alt="Karine Lauzon RN"
                className="w-full h-56 md:h-64 object-cover rounded-lg mb-3"
                data-testid="karine-clinic-banner"
              />
              <h3 className="font-semibold text-lg text-gray-800" style={{ fontFamily: "'Playfair Display', serif" }}>
                Karine Lauzon, RN
              </h3>
              <p className="text-xs text-amber-700 font-medium tracking-wide uppercase mt-0.5">Precision Injectionist</p>
              <p className="text-sm text-gray-600 mt-1">
                {language === "fr" ? "35+ ans d'expertise en soins infirmiers" : "35+ years of nursing expertise"}
              </p>
              <div className="flex justify-center gap-1 mt-2">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-amber-400">★</span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">100+ 5-Star Reviews</p>

              <div className="mt-3 space-y-1.5 text-left">
                <p className="text-xs text-gray-600 flex items-start gap-1.5">
                  <span className="text-amber-500 flex-shrink-0 mt-0.5">✦</span>
                  {language === "fr" ? "Spécialiste Botox, Fillers, Sculptra & PRP" : "Botox, Fillers, Sculptra & PRP Specialist"}
                </p>
                <p className="text-xs text-gray-600 flex items-start gap-1.5">
                  <span className="text-amber-500 flex-shrink-0 mt-0.5">✦</span>
                  {language === "fr" ? "Approche naturelle — jamais figé, jamais exagéré" : "Natural approach — never frozen, never overdone"}
                </p>
                <p className="text-xs text-gray-600 flex items-start gap-1.5">
                  <span className="text-amber-500 flex-shrink-0 mt-0.5">✦</span>
                  {language === "fr" ? "3 cliniques: Ottawa, Kanata, Carleton Place" : "3 clinics: Ottawa, Kanata, Carleton Place"}
                </p>
                <p className="text-xs text-gray-600 flex items-start gap-1.5">
                  <span className="text-amber-500 flex-shrink-0 mt-0.5">✦</span>
                  {language === "fr" ? "Consultations GRATUITES disponibles" : "FREE consultations available"}
                </p>
              </div>
            </div>
            <div className="p-3 bg-amber-50 mt-auto">
              <button onClick={openBookingModal}
                className="block w-full text-center bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md cursor-pointer"
                data-testid="promo-book-now-center">
                {language === "fr" ? "Consultation Gratuite" : "Free Consultation"}
              </button>
            </div>
          </div>
          
          {/* Summer Glow Combo Pack - Right */}
          <div className="rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border-2 border-emerald-300 flex flex-col" data-testid="spring-glow-promo-box">
            <div className="flex-1">
              <img 
                src={SPRING_GLOW_IMAGE}
                alt={language === "fr" ? "Summer Glow Combo Pack — Crow's Feet + Number 11's $349" : "Summer Glow Combo Pack — Crow's Feet + Number 11's $349"}
                className="w-full h-full object-cover"
                data-testid="spring-glow-banner-right"
              />
            </div>
            <div className="p-3 bg-emerald-50">
              <button onClick={openBookingModal}
                className="block w-full text-center bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md cursor-pointer"
                data-testid="promo-book-now-spring-glow">
                {language === "fr" ? "Reserver Maintenant" : "Book Now — Summer Glow"}
              </button>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          <div className="flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full">
            <span className="text-pink-600">✨</span>
            <span className="text-sm text-pink-800">{language === "fr" ? "Résultats naturels" : "Natural-looking results"}</span>
          </div>
          <div className="flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full">
            <span className="text-pink-600">✨</span>
            <span className="text-sm text-pink-800">{language === "fr" ? "Plans personnalisés" : "Personalized treatment plans"}</span>
          </div>
          <div className="flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full">
            <span className="text-pink-600">✨</span>
            <span className="text-sm text-pink-800">{language === "fr" ? "Soins médicaux experts" : "Medical-grade expert care"}</span>
          </div>
          <div className="flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full">
            <span className="text-pink-600">✨</span>
            <span className="text-sm text-pink-800">{language === "fr" ? "Prix saisonnier limité" : "Limited seasonal pricing"}</span>
          </div>
        </div>

        {/* Summer Botox Refresh Packages */}
        <div className="mb-10">
          <h3 className="text-xl font-semibold mb-2 text-center flex items-center justify-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span>💉</span>
            {language === "fr" ? "FORFAITS BOTOX ÉTÉ" : "SUMMER BOTOX REFRESH PACKAGES"}
          </h3>
          <p className="text-center text-sm text-muted-foreground mb-6">
            {language === "fr" ? "Rafraîchissez. Restaurez. Rajeunissez." : "Refresh. Restore. Rejuvenate."} 🌷
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {springRefreshPackages.map((pkg) => {
              const inCart = isInCart(pkg.id);
              return (
                <div
                  key={pkg.id}
                  onClick={() => handlePackageClick(pkg)}
                  className={`bg-card border-2 rounded-xl p-5 hover:shadow-lg transition-all duration-300 relative cursor-pointer ${
                    inCart 
                      ? "border-pink-500 bg-pink-50 ring-2 ring-pink-200" 
                      : "border-pink-200 hover:border-pink-400"
                  }`}
                  data-testid={`promo-${pkg.id}`}
                >
                  {pkg.tag && (
                    <span className="absolute -top-2 right-4 bg-pink-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                      {getTag(pkg.tag)}
                    </span>
                  )}
                  
                  <div className={pkg.tag ? "pt-2" : ""}>
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <h4 className="font-semibold text-base text-pink-900">{getText(pkg.title)}</h4>
                        {pkg.subtitle && (
                          <p className="text-sm text-pink-600 italic">{getText(pkg.subtitle)}</p>
                        )}
                      </div>
                      <span className="text-xl font-bold text-pink-700 whitespace-nowrap ml-2">{pkg.price}<span className="text-xs">+tx</span></span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 mb-1">
                      <span className="bg-black text-[#b2fce4] text-[9px] font-bold px-1.5 py-0.5 rounded">afterpay</span>
                      <span className="text-xs text-gray-500">4 x ${(parseFloat(pkg.price.replace(/[^0-9.]/g, '')) / 4).toFixed(2)}</span>
                      <span className="text-amber-600 text-[11px] font-semibold flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                        Earn {Math.floor(parseFloat(pkg.price.replace(/[^0-9.]/g, '')))} pts
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2 mt-2">{getText(pkg.areas)}</p>
                    {pkg.note && (
                      <p className="text-xs text-pink-600/80 italic mb-2">{getText(pkg.note)}</p>
                    )}
                    <button
                      className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full transition-all ${
                        inCart
                          ? "bg-pink-600 text-white"
                          : "bg-pink-100 text-pink-700 hover:bg-pink-200"
                      }`}
                    >
                      {inCart ? (
                        <>
                          <Check className="w-3 h-3" />
                          {t("misc.added")}
                        </>
                      ) : (
                        <>
                          <Plus className="w-3 h-3" />
                          {t("misc.addToAppointment")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summer Filler Deals */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-2 text-center flex items-center justify-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span>💎</span>
            {language === "fr" ? "FORFAITS FILLER ÉTÉ" : "DERMAL FILLER SUMMER PACKAGES"}
          </h3>
          <p className="text-center text-sm text-muted-foreground mb-6">
            {language === "fr" ? "Achetez maintenant et économisez vos seringues" : "Purchase now and bank your syringes"}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {fillerDeals.map((deal) => {
              const inCart = isInCart(deal.id);
              return (
                <div
                  key={deal.id}
                  onClick={() => handlePackageClick(deal)}
                  className={`bg-card border-2 rounded-xl p-4 hover:shadow-lg transition-all duration-300 relative cursor-pointer text-center ${
                    inCart 
                      ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200" 
                      : deal.tag === "Best Value"
                        ? "border-amber-400 bg-amber-50"
                        : "border-gray-200 hover:border-amber-300"
                  }`}
                  data-testid={`promo-${deal.id}`}
                >
                  {deal.tag && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                      ⭐ {getTag(deal.tag)}
                    </span>
                  )}
                  
                  <div className={deal.tag ? "pt-2" : ""}>
                    <h4 className="font-semibold text-sm text-gray-800">{getText(deal.title)}</h4>
                    <span className="text-lg font-bold text-amber-700 block my-2">{deal.price}<span className="text-xs">+tx</span></span>
                    <button
                      className={`flex items-center justify-center gap-1 text-xs font-medium px-2 py-1 rounded-full transition-all w-full ${
                        inCart
                          ? "bg-amber-600 text-white"
                          : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                      }`}
                    >
                      {inCart ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Locations & CTA */}
        <div className="text-center mt-8 bg-gradient-to-r from-pink-100 to-amber-100 rounded-2xl p-6">
          <p className="text-sm text-gray-600 mb-3">
            📍 {language === "fr" ? "Visitez-nous à l'un de nos emplacements:" : "Visit us at any of our locations:"}
          </p>
          <p className="font-semibold text-gray-800 mb-4">Ottawa | Kanata | Carleton Place</p>
          <Button
            onClick={() => scrollToSection("locations")}
            className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white px-8 py-5 text-lg"
            data-testid="book-promo"
          >
            {language === "fr" ? "Réservez Maintenant" : "Book Now"} →
          </Button>
          <p className="text-pink-700 mt-4 font-medium">
            🌷 {language === "fr" ? "Rafraîchissez. Restaurez. Rajeunissez." : "Refresh. Restore. Rejuvenate."}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PromotionsSection;
